import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  authApi,
  getToken,
  getUserCache,
  isTokenExpired,
  USER_TIERS,
  USER_STATUS,
  hasMinimumTier
} from '../shared/auth';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewAsTier, setViewAsTier] = useState(null);

  // 사용자 정보 로드
  const loadUser = useCallback(async () => {
    const token = getToken();

    if (!token) {
      setLoading(false);
      return;
    }

    // 토큰 만료 확인
    if (isTokenExpired(token)) {
      authApi.logout();
      setLoading(false);
      return;
    }

    try {
      // API에서 사용자 정보 조회
      const userData = await authApi.getMe();
      setUser(userData);
    } catch (err) {
      console.error('Failed to load user:', err);

      // API 실패 시 캐시에서 복원 시도
      const cached = getUserCache();
      if (cached) {
        setUser(cached);
      } else {
        authApi.logout();
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  // 로그인
  const login = async (email, password) => {
    try {
      setError(null);
      const userData = await authApi.login(email, password);
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // 소셜 로그인
  const socialLogin = async (provider, socialUser) => {
    try {
      setError(null);
      const userData = await authApi.socialLogin(provider, socialUser);
      setUser(userData);
      return userData;
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  // 로그아웃
  const logout = () => {
    authApi.logout();
    setUser(null);
  };

  // 사용자 정보 갱신
  const refreshUser = async () => {
    try {
      const userData = await authApi.getMe();
      setUser(userData);
      return userData;
    } catch (err) {
      console.error('Failed to refresh user:', err);
      throw err;
    }
  };

  // 토큰 조회 (API 호출용)
  const getAuthToken = getToken;

  // 인증 상태
  const isAuthenticated = !!user;
  const isAdmin = user?.tier === USER_TIERS.ADMIN;
  const isFamily = user?.tier === USER_TIERS.FAMILY || isAdmin;
  const isSubscriber = hasMinimumTier(user?.tier, USER_TIERS.SUBSCRIBER);

  // Access Control
  const hasAccess = (requiredTiers) => {
    if (!user) return false;
    const effectiveTier = viewAsTier || user.tier;
    return requiredTiers.includes(effectiveTier);
  };

  // Admin Functions - Tier Simulation (for testing access control)
  const setViewAs = (tier) => {
    if (!user || user.tier !== USER_TIERS.ADMIN) return;
    setViewAsTier(tier);
  };

  const resetViewAs = () => setViewAsTier(null);
  const getActualTier = () => user?.tier;

  const value = {
    user,
    loading,
    error,
    login,
    socialLogin,
    logout,
    refreshUser,
    getToken: getAuthToken,
    isAuthenticated,
    isAdmin,
    isFamily,
    isSubscriber,
    USER_TIERS,
    USER_STATUS,
    hasMinimumTier,
    // Access Control
    hasAccess,
    viewAsTier,
    // Admin - Tier Simulation
    setViewAs,
    resetViewAs,
    getActualTier
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export { USER_TIERS, USER_STATUS };
